import type { ReactElement } from 'react';
import { Easing, interpolate, useCurrentFrame } from 'remotion';

// Types
import { TextSegment } from '../type';

// Utils
import { splitSegmentsIntoWords } from '../utils';

// Constants
import { CLAMP_BOTH } from '@/constants/animations';
import {
  HIGHLIGHT_TRIGGER_FRACTION,
  SHIMMER_DELAY_AFTER_HIGHLIGHT,
  SHIMMER_DURATION,
  WORD_FADE_DURATION,
  WORD_FADE_DURATION_SLOW,
  WORD_SLIDE_DISTANCE,
} from '../constants';

interface HighlightedTextLineProps {
  segments: TextSegment[];
  delay: number;
  framesPerWord?: number;
  highlightDelay: number;
  highlightDuration: number;
  isLastLine?: boolean;
}

/**
 * Renders a line of text with word-by-word fade-in animation and optional highlighted segments.
 */
export const HighlightedTextLine = ({
  segments,
  delay,
  framesPerWord = 6,
  highlightDelay,
  highlightDuration,
  isLastLine = false,
}: HighlightedTextLineProps): ReactElement => {
  const frame = useCurrentFrame();

  const words = splitSegmentsIntoWords(segments);

  const fadeDuration = isLastLine
    ? WORD_FADE_DURATION_SLOW
    : WORD_FADE_DURATION;

  // Track the index of non-whitespace words for staggering
  let nonSpaceWordIndex = 0;

  // All non-whitespace words for global index lookup
  const allNonSpaceWords = words.filter((w) => w.word.trim().length > 0);

  // Group words back by their original segment for rendering
  const segmentWordGroups = segments.map((segment, segIdx) => ({
    segment,
    words: words.filter((w) => w.segmentIndex === segIdx),
  }));

  return (
    <div className="flex flex-nowrap items-baseline justify-start whitespace-nowrap">
      {segmentWordGroups.map(({ segment, words: segWords }, segIdx) => {
        // Calculate per-segment highlight timing based on when this segment's last word finishes
        const segNonSpaceWords = segWords.filter(
          (w) => w.word.trim().length > 0,
        );

        // Find the global index of the last non-space word in this segment
        const lastSegWord = segNonSpaceWords[segNonSpaceWords.length - 1];
        const lastSegWordGlobalIndex =
          lastSegWord !== undefined
            ? allNonSpaceWords.indexOf(lastSegWord)
            : -1;

        // Calculate when this segment's last word is sufficiently visible
        const segmentRevealedFrame =
          lastSegWordGlobalIndex >= 0
            ? delay +
              lastSegWordGlobalIndex * framesPerWord +
              fadeDuration * HIGHLIGHT_TRIGGER_FRACTION
            : 0;

        // Highlight background animation (starts after this segment's words are revealed)
        const segHighlightStart = segmentRevealedFrame + highlightDelay;

        const segHighlightOpacity = interpolate(
          frame,
          [segHighlightStart, segHighlightStart + highlightDuration],
          [0, 1],
          CLAMP_BOTH,
        );

        // Shimmer sweep across highlight background
        const segShimmerStart =
          segHighlightStart + highlightDuration + SHIMMER_DELAY_AFTER_HIGHLIGHT;
        const segShimmerProgress = interpolate(
          frame,
          [segShimmerStart, segShimmerStart + SHIMMER_DURATION],
          [-0.3, 1.3],
          CLAMP_BOTH,
        );

        return (
          <span
            key={segIdx}
            className={`relative inline-block text-[90px] leading-[1.3] text-white ${
              segment.highlighted ? 'px-4 py-1' : 'px-1.5 py-0'
            }`}
          >
            {/* Highlight background - always rendered, visibility controlled by opacity */}
            {segment.highlighted && (
              <span
                className="absolute inset-0 overflow-hidden bg-secondary-250"
                style={{
                  opacity: segHighlightOpacity,
                }}
              >
                {/* Shimmer light sweep */}
                <span
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(
                      105deg,
                      transparent 0%,
                      transparent ${(segShimmerProgress - 0.15) * 100}%,
                      rgba(255, 255, 255, 0.12) ${segShimmerProgress * 100}%,
                      transparent ${(segShimmerProgress + 0.15) * 100}%,
                      transparent 100%
                    )`,
                  }}
                />
              </span>
            )}

            {/* Words with fade-in animation */}
            <span className="relative z-10">
              {segWords.map((wordToken, wordIdx) => {
                const isWhitespace = wordToken.word.trim().length === 0;

                // Whitespace tokens render immediately (no animation)
                if (isWhitespace) {
                  return (
                    <span key={wordIdx} className="inline whitespace-pre">
                      {wordToken.word}
                    </span>
                  );
                }

                const currentWordIndex = nonSpaceWordIndex;
                nonSpaceWordIndex++;

                const wordStartFrame = delay + currentWordIndex * framesPerWord;

                // Smooth fade-in with ease-out
                const rawProgress = interpolate(
                  frame,
                  [wordStartFrame, wordStartFrame + fadeDuration],
                  [0, 1],
                  CLAMP_BOTH,
                );
                const easedProgress = Easing.out(Easing.cubic)(rawProgress);

                const wordOpacity = easedProgress;
                const wordTranslateY = interpolate(
                  easedProgress,
                  [0, 1],
                  [WORD_SLIDE_DISTANCE, 0],
                );
                const wordScale = interpolate(easedProgress, [0, 1], [0.96, 1]);

                return (
                  <span
                    key={wordIdx}
                    className="inline-block whitespace-pre"
                    style={{
                      opacity: wordOpacity,
                      transform: `translateY(${wordTranslateY}px) scale(${wordScale})`,
                      filter: `blur(${interpolate(easedProgress, [0, 1], [2, 0])}px)`,
                    }}
                  >
                    {wordToken.word}
                  </span>
                );
              })}
            </span>
          </span>
        );
      })}
    </div>
  );
};
